import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { cloneDeep } from 'lodash'

import { toast } from '@/components/toasts/toast.helpers'
import {
  createEntry,
  reorderEntry,
  reorderAndMoveEntry,
  deleteEntry,
  updateCategoryName,
} from '@/reducers/playbooks/playbook.redux'
import { fetchPlaybookSectionWarnings } from '@/reducers/playbooks/playbook.actions'
import { reorder } from '@/utils/helpers'

import { CategoryEntryDisplay } from './CategoryEntryDisplay'
import { CategoriesSidebarDisplay } from './CategoriesSidebarDisplay'
import { CategoryHeader } from './CategoryHeader'
import {
  categoryTriggerTypes,
  getUpdatedSourceAndDestination,
  accessorSectionMap,
  accessors,
} from '../../playbook.helpers'
import { PlaybookEntry } from '../../components/PlaybookEntry'
import { SectionWarnings } from '../../components/SectionWarnings'

export const CategoryDisplay = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { section, id } = useParams()
  const {
    playbook,
    playbook: { current: isLatestPlaybookVersion },
    warnings,
    loading,
    validationError,
  } = useSelector((state) => state.playbook)
  const { edit_config } = useSelector((state) => state.currentUser)
  const accessor = accessorSectionMap[section]
  const categories = playbook.body[accessor]
  const readOnly = !isLatestPlaybookVersion || !edit_config
  const updatedCategoryTriggerTypes = [
    ...categoryTriggerTypes,
    { label: 'Smart Phrases', value: 'smart_phrases' },
  ]

  const handleSidebarReorder = (newOrder) => {
    dispatch(reorderEntry(accessor)(newOrder))
  }

  const handleDragEnd = (result) => {
    const { type, source, destination } = result

    if (!destination) {
      return
    }

    if (type === 'droppable-entry') {
      const sourceCategoryId = source.droppableId
      const destinationCategoryId = destination.droppableId

      // If drag and dropping within the same category
      if (sourceCategoryId === destinationCategoryId) {
        const originalOrder = categories.entries[sourceCategoryId].order
        const newOrder = reorder(originalOrder, source.index, destination.index)

        dispatch(reorderEntry(accessor)({ categoryId: sourceCategoryId, order: newOrder }))
      } else {
        // If drag and dropping to a new category
        const updatedEntries = getUpdatedSourceAndDestination({
          entries: cloneDeep(categories.entries),
          source,
          destination,
          sourceCategoryId,
          destinationCategoryId,
        })

        dispatch(reorderAndMoveEntry(accessor)(updatedEntries))
      }
    }

    // Reordering categories
    if (type === 'droppable-category') {
      const originalOrder = categories.order
      const newOrder = reorder(originalOrder, source.index, destination.index)

      dispatch(reorderEntry(accessor)(newOrder))
    }
  }

  const handleDelete = (categoryId) => {
    dispatch(deleteEntry(accessor)(categoryId))
    toast.success('Category and entries have been deleted')
    dispatch(fetchPlaybookSectionWarnings(accessor))
  }

  const handleRename = ({ name, categoryId }) => {
    dispatch(updateCategoryName(accessor)({ name, categoryId }))
  }

  const handleCreateItem = (categoryId) => {
    history.push(`/playbooks/${id}/${section}/create?category_id=${categoryId}`)
  }

  const renderCategoryHeader = (categoryId, category) => {
    return (
      <CategoryHeader
        categoryId={categoryId}
        category={category}
        readOnly={readOnly}
        nestedEntriesCount={category.order.length}
        validationError={validationError}
        accessor={accessor}
        onDelete={handleDelete}
        onRename={handleRename}
        onCreateItem={handleCreateItem}
      />
    )
  }

  const renderEntry = (entry, entryId, categoryId) => {
    return (
      <PlaybookEntry
        accessor={accessor}
        categoryId={categoryId}
        key={entryId}
        entryId={entryId}
        entry={entry}
        isWinnable={accessor === accessors.POSTCALL}
        label={accessor === accessors.DYNAMIC_PROMPT ? 'dynamic prompt' : 'post call item'}
        triggerTypes={updatedCategoryTriggerTypes}
        readOnly={readOnly}
      />
    )
  }

  return (
    <div className="playbook-category-and-entry-wrapper">
      <CategoriesSidebarDisplay
        categories={categories}
        readOnly={readOnly}
        onReorder={handleSidebarReorder}
        createAction={createEntry(accessor)}
      />
      <div className="playbook-entries">
        {isLatestPlaybookVersion && (
          <SectionWarnings
            section={section}
            warnings={warnings}
            loading={loading.warnings}
            style={{ margin: '0px calc(30px + 0.75rem) 1rem calc(30px + 0.75rem)' }}
          />
        )}
        <CategoryEntryDisplay
          categories={categories}
          readOnly={readOnly}
          onDragEnd={handleDragEnd}
          renderCategoryHeader={renderCategoryHeader}
          renderEntry={renderEntry}
        />
      </div>
    </div>
  )
}
